import React, { useMemo } from "react";

/**
 * Tokenize a string into words + separators, keeping whitespace/punctuation
 * as their own tokens so we can render a clean inline diff.
 */
function tokenize(s) {
  if (!s) return [];
  // Split keeping word, whitespace, and single punctuation chars as tokens
  return s.match(/[\p{L}\p{N}]+|\s+|[^\p{L}\p{N}\s]/gu) || [];
}

/**
 * Classic LCS-based diff over token arrays. Returns an array of
 * { type: "equal" | "del" | "add", text: string } chunks (consecutive
 * tokens of the same type are merged for compact rendering).
 */
function diffTokens(a, b) {
  const n = a.length;
  const m = b.length;
  // Build LCS length table (rolling not needed — bounded by short text)
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push("equal", a[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push("del", a[i]);
      i++;
    } else {
      push("add", b[j]);
      j++;
    }
  }
  while (i < n) {
    push("del", a[i]);
    i++;
  }
  while (j < m) {
    push("add", b[j]);
    j++;
  }
  return out;
}

/**
 * Inline word-level diff renderer. Removed tokens are shown with a red
 * strike-through and added tokens are highlighted in green. Identical
 * content is rendered as plain text.
 *
 * Props:
 *   original: string
 *   suggested: string
 *   className?: string
 */
export default function WordDiff({ original = "", suggested = "", className = "" }) {
  const chunks = useMemo(
    () => diffTokens(tokenize(original), tokenize(suggested)),
    [original, suggested],
  );
  if (!original && !suggested) {
    return (
      <pre className={`text-xs font-mono whitespace-pre-wrap text-neutral-400 ${className}`}>
        —
      </pre>
    );
  }
  return (
    <pre
      className={`text-xs font-mono whitespace-pre-wrap text-neutral-900 leading-relaxed ${className}`}
      data-testid="word-diff"
    >
      {chunks.map((c, idx) => {
        if (c.type === "equal") {
          return (
            <span key={idx} className="text-neutral-700">
              {c.text}
            </span>
          );
        }
        if (c.type === "del") {
          // Keep whitespace tokens visible but unhighlighted to avoid
          // confusing strikes on bare spaces.
          if (/^\s+$/.test(c.text)) return <span key={idx}>{c.text}</span>;
          return (
            <span
              key={idx}
              className="bg-red-100 text-red-900 line-through decoration-red-700/70 px-0.5"
              data-testid="diff-del"
            >
              {c.text}
            </span>
          );
        }
        // add
        if (/^\s+$/.test(c.text)) return <span key={idx}>{c.text}</span>;
        return (
          <span
            key={idx}
            className="bg-emerald-100 text-emerald-900 px-0.5"
            data-testid="diff-add"
          >
            {c.text}
          </span>
        );
      })}
    </pre>
  );
}
